"use client";

import { useState } from "react";
import { DownloadIcon, FileDownIcon } from "lucide-react";
import dynamic from "next/dynamic";

import type { TourBrochureView } from "@tripcart/shared";
import { cx } from "@tripcart/traveler-ui/cx";
import { Button } from "@tripcart/traveler-ui/components/button";

import { useTranslator } from "@/lib/translation-context";

import styles from "./brochure.module.css";
import {
  type DownloadStatus,
  useBrochureDownload,
} from "./use-brochure-download";

// Most visits never open the popup; its form ships only when it does.
const BrochureLeadDialog = dynamic(() =>
  import("./brochure-lead-dialog.client").then((m) => m.BrochureLeadDialog),
);

const CLASSES = {
  overview: {
    box: styles.card,
    mark: styles.mark,
    title: styles.title,
    desc: styles.desc,
    button: styles.wrapButton,
    buttonSize: "lg",
  },
  rail: {
    box: styles.rail,
    mark: styles.railMark,
    title: styles.railTitle,
    desc: styles.railDesc,
    button: cx(styles.wrapButton, styles.railButton),
    buttonSize: "default",
  },
} as const;

function StatusLine({ status }: { status: DownloadStatus | null }) {
  if (!status) return null;
  return (
    <p
      role="status"
      className={cx(
        styles.status,
        status.kind === "error" && styles.statusError,
      )}
    >
      {status.message}
    </p>
  );
}

function CardLayout({
  variant,
  brochure,
  button,
  status,
}: {
  variant: "overview" | "rail";
  brochure: TourBrochureView;
  button: React.ReactNode;
  status: DownloadStatus | null;
}) {
  const c = CLASSES[variant];
  const action = (
    <>
      {button}
      <StatusLine status={status} />
    </>
  );
  return (
    <aside
      className={cx(
        c.box,
        variant === "overview" &&
          brochure.placement === "rail" &&
          styles.mobileOnly,
      )}
    >
      <span className={c.mark} aria-hidden>
        <FileDownIcon />
      </span>
      <div className={styles.text}>
        <div className={c.title}>{brochure.title}</div>
        {brochure.description.trim() ? (
          <p className={c.desc}>{brochure.description}</p>
        ) : null}
        {variant === "rail" ? action : null}
      </div>
      {variant === "overview" ? (
        <div className={styles.actions}>{action}</div>
      ) : null}
    </aside>
  );
}

/** The Tour Brochure app's download card (prototype `.bro` / `.bro-rail`).
 *  `overview` sits at the end of the Overview section; `rail` sits under the
 *  booking box and is hidden below the rail breakpoint (`styles.rail`), so a
 *  phone sees one card: with placement "rail" the overview card steps in
 *  there (`styles.mobileOnly`). */
export function BrochureCard({
  brochure,
  slug,
  tourName,
  variant,
}: {
  brochure: TourBrochureView;
  slug: string;
  tourName: string;
  variant: "overview" | "rail";
}) {
  const t = useTranslator();
  const [open, setOpen] = useState(false);
  const dl = useBrochureDownload(slug, brochure.popup !== null);

  function click() {
    if (brochure.popup && !dl.lead) setOpen(true);
    else void dl.download(dl.lead);
  }

  const label = dl.locked
    ? `${brochure.buttonLabel}${t(", asks for your name and email first")}`
    : brochure.buttonLabel;
  const button = (
    <Button
      variant="outline"
      tone="brand"
      size={CLASSES[variant].buttonSize}
      aria-label={label}
      disabled={dl.busy}
      onClick={click}
      className={CLASSES[variant].button}
    >
      <DownloadIcon aria-hidden />
      {brochure.buttonLabel}
    </Button>
  );
  // While the popup is open its own line shows the error; the card shows the
  // outcome once it closes (success) or when there was no popup at all.
  const status = open ? null : dl.status;

  return (
    <>
      <CardLayout
        variant={variant}
        brochure={brochure}
        button={button}
        status={status}
      />
      {brochure.popup && open ? (
        <BrochureLeadDialog
          open={open}
          onOpenChange={setOpen}
          popup={brochure.popup}
          tourName={tourName}
          busy={dl.busy}
          serverError={dl.status?.kind === "error" ? dl.status.message : null}
          onSubmit={dl.download}
        />
      ) : null}
    </>
  );
}
